/**
 * Mixpanel Analytics Service
 * 
 * Wrapper around Mixpanel browser SDK for product analytics tracking
 */

import mixpanel from 'mixpanel-browser';
import { isDevelopmentMode } from '@/lib/utils';

let isInitialized = false;

/**
 * Initialize Mixpanel with API key
 * Should be called once at application startup
 */
export const initMixpanel = (): void => {
  const apiKey = import.meta.env.VITE_REACT_APP_MIXPANEL_API_TOKEN;

  if (!apiKey) {
    console.warn('[Mixpanel] API key not found, Mixpanel will not be initialized');
    console.warn('[Mixpanel] Please add VITE_REACT_APP_MIXPANEL_API_KEY to your .env.development file');
    return;
  }

  try {
    const isDev = isDevelopmentMode();
    mixpanel.init(apiKey, {
      debug: isDev,
      ignore_dnt: true, // Respect user privacy preferences
      // Session Replay: Manual capture mode (not enabled by default)
      // Set record_sessions_percent to 0 to disable automatic recording
      record_sessions_percent: 0,
      // Session Replay privacy: unmask non-input text globally for new recordings.
      // Note: manual start_session_recording() uses these init-time replay settings.
      record_mask_all_text: false,
    } as any);

    // Set environment as super property
    const environment = import.meta.env.MODE === 'production' ? 'production' : 
                       import.meta.env.MODE === 'staging' ? 'staging' :
                       import.meta.env.MODE === 'vendor' ? 'vendor' : 'development';
    mixpanel.register({ environment });

    isInitialized = true;
    console.log('[Mixpanel] Initialized successfully', { environment, apiKeyPrefix: apiKey.substring(0, 8) + '...' });
  } catch (error) {
    console.error('[Mixpanel] Initialization failed:', error);
  }
};

/**
 * Track an event with optional properties
 */
export const trackEvent = (eventName: string, properties?: Record<string, unknown>): void => {
  if (!isInitialized) {
    console.warn('[Mixpanel] Not initialized, event not tracked:', eventName);
    return;
  }

  try {
    mixpanel.track(eventName, properties);
    const isDev = isDevelopmentMode();
    if (isDev) {
      console.log('[Mixpanel] Event tracked:', eventName, properties);
    }
  } catch (error) {
    console.error('[Mixpanel] Failed to track event:', error);
  }
};

/**
 * Identify a user with a unique ID and optional properties
 * Automatically aliases anonymous events to the user ID before identifying
 */
export const identify = (userId: string, properties?: Record<string, unknown>): void => {
  if (!isInitialized) {
    return;
  }

  try {
    // Get current distinct_id (could be anonymous device ID)
    const currentDistinctId = mixpanel.get_distinct_id();
    
    // If current ID is anonymous and different from user ID, alias them
    // This links any anonymous events from the current session to the user
    if (currentDistinctId && currentDistinctId !== userId) {
      // Check if this looks like an anonymous ID
      const isAnonymousId = currentDistinctId.startsWith('$device:') || 
                           currentDistinctId.startsWith('$distinct_id:') ||
                           /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(currentDistinctId);
      
      if (isAnonymousId) {
        // Alias the anonymous ID to the user ID
        // This merges anonymous events into the user's profile
        mixpanel.alias(userId, currentDistinctId);
      }
    }
    
    // Now identify with the user ID
    mixpanel.identify(userId);
    
    if (properties) {
      mixpanel.people.set(properties);
    }
  } catch (error) {
    console.error('[Mixpanel] Failed to identify user:', error);
  }
};

/**
 * Reset user identity (call on logout)
 */
export const reset = (): void => {
  if (!isInitialized) {
    return;
  }

  try {
    mixpanel.reset();
  } catch (error) {
    console.error('[Mixpanel] Failed to reset:', error);
  }
};

/**
 * Set user properties (updates existing user profile)
 */
export const setUserProperties = (properties: Record<string, unknown>): void => {
  if (!isInitialized) {
    return;
  }

  try {
    mixpanel.people.set(properties);
  } catch (error) {
    console.error('[Mixpanel] Failed to set user properties:', error);
  }
};

/**
 * Set super properties (included with all events)
 */
export const setSuperProperties = (properties: Record<string, unknown>): void => {
  if (!isInitialized) {
    return;
  }

  try {
    mixpanel.register(properties);
  } catch (error) {
    console.error('[Mixpanel] Failed to set super properties:', error);
  }
};

/**
 * Start session recording (manual capture)
 * Forces recording to begin regardless of record_sessions_percent setting
 * Returns true if recording was started, false if already active or failed
 */
export const startSessionRecording = (): boolean => {
  if (!isInitialized) {
    console.warn('[Mixpanel] Not initialized, cannot start session recording');
    return false;
  }

  // Check if recording is already active (but don't block if localStorage says it is)
  const props = getSessionRecordingProperties();
  const hasReplayId = !!(props && props.$session_replay_id);
  if (hasReplayId) {
    console.log('[Mixpanel] Recording already in progress (has replay ID), skipping start');
    setRecordingState(true);
    return false;
  }

  try {
    // Type assertion for session replay methods that may not be in type definitions
    (mixpanel as any).start_session_recording();
    console.log('[Mixpanel] Session recording started');
    // Set state optimistically - recording should be active now
    setRecordingState(true);
    return true;
  } catch (error) {
    console.error('[Mixpanel] Failed to start session recording:', error);
    setRecordingState(false);
    return false;
  }
};

/**
 * Stop session recording (manual capture)
 */
export const stopSessionRecording = (): void => {
  if (!isInitialized) {
    console.warn('[Mixpanel] Not initialized, cannot stop session recording');
    setRecordingState(false);
    return;
  }

  try {
    // Type assertion for session replay methods that may not be in type definitions
    (mixpanel as any).stop_session_recording();
    console.log('[Mixpanel] Session recording stopped');
    // Clear state - recording is now stopped
    setRecordingState(false);
  } catch (error) {
    console.error('[Mixpanel] Failed to stop session recording:', error);
    // Still clear state even if there was an error
    setRecordingState(false);
  }
};

/**
 * Get session recording properties
 * Returns properties that should be included with events to link them to session replays
 */
export const getSessionRecordingProperties = (): Record<string, unknown> => {
  if (!isInitialized) {
    return {};
  }

  try {
    // Type assertion for session replay methods that may not be in type definitions
    return (mixpanel as any).get_session_recording_properties() || {};
  } catch (error) {
    console.error('[Mixpanel] Failed to get session recording properties:', error);
    return {};
  }
};

/**
 * Check if session recording is currently active
 * Returns true if recording is in progress
 * Note: This checks Mixpanel's properties, but may not be reliable immediately after starting
 */
export const isSessionRecordingActive = (): boolean => {
  if (!isInitialized) {
    return false;
  }

  try {
    const props = getSessionRecordingProperties();
    const hasReplayId = !!(props && props.$session_replay_id);
    // Also check localStorage as a fallback
    const storedState = localStorage.getItem('mixpanel_recording_active');
    const isStoredActive = storedState === 'true';
    
    // Return true if either Mixpanel says it's active OR localStorage says it's active
    // This handles the case where Mixpanel hasn't initialized the replay ID yet
    return hasReplayId || isStoredActive;
  } catch (error) {
    console.error('[Mixpanel] Failed to check recording status:', error);
    // Fallback to localStorage
    const storedState = localStorage.getItem('mixpanel_recording_active');
    return storedState === 'true';
  }
};

/**
 * Set the recording state in localStorage
 * This helps persist state across navigation
 */
export const setRecordingState = (active: boolean): void => {
  try {
    if (active) {
      localStorage.setItem('mixpanel_recording_active', 'true');
    } else {
      localStorage.removeItem('mixpanel_recording_active');
    }
  } catch (error) {
    console.error('[Mixpanel] Failed to set recording state:', error);
  }
};
